/** Source detail (/w/:ws/knowledge/:sourceId) — tenant/knowledge-detail.html. */
export const sourceDetail = {
  title: "Source details",
  back: "Back to Knowledge Base",
  usedBy: "Used by",
  usedByNone: "Not linked to any agent yet",
  and: "and",
  version: "v{{version}}",
  notFoundTitle: "This source doesn't exist anymore",
  notFoundBody: "It may have been deleted. Go back to the Knowledge Base to see your current sources.",
  loadError: "This source couldn't load.",
  status: {
    ready: "Ready",
    processing: "Processing",
    queued: "Queued",
    failed: "Failed",
    disabled: "Disabled",
    readyWithWarnings: "Ready with warnings",
    processingPages: "Processing · {{done}}/{{total}} pages",
  },
  stages: {
    uploading: "Uploading",
    parsing: "Parsing (OCR)",
    chunking: "Chunking",
    contextualizing: "Contextualizing",
    embedding: "Embedding",
    ready: "Ready",
    failed: "Failed",
  },
  types: {
    file: "File",
    website: "Website",
    qa: "Q&A",
    text: "Text snippet",
    drive: "Google Drive",
  },
  actions: {
    replace: "Replace file",
    reindex: "Re-index",
    reindexing: "Re-indexing — the current version stays live until it finishes",
    recrawling: "Re-crawl queued — the current pages stay live until it finishes",
    download: "Download",
    delete: "Delete",
  },
  details: {
    title: "Details",
    type: "Type",
    pages: "Pages",
    size: "Size",
    url: "Address",
    version: "Version",
    versionValue: "{{version}} · {{date}}",
    language: "Language detected",
    languageUnknown: "Not detected yet",
    lastSynced: "Last synced",
    validity: "Valid",
    validityRange: "{{from}} – {{until}}",
    chunks: "Chunks",
    excluded: "({{count}} excluded)",
    warnings: "Warnings",
  },
  access: {
    title: "Access control",
    inherit: "Inherit from agent",
    inheritHint: "Use each agent's default audience instead of a fixed set.",
    inheritUnknown: "You need access to Audiences & Access to change inheritance.",
    who: "Who can retrieve this document",
    whoHint: "Answers are filtered by audience before the AI sees this source.",
    atLeastOne: "Pick at least one audience",
    agentMapping: "{{agent}} → {{audiences}}",
    save: "Save access",
    saved: "Access saved — chunks re-tagged without re-embedding",
    inheritOn: "This document now follows each agent's audience",
  },
  timeline: {
    title: "Processing timeline",
    total: "v{{version}}",
    uploaded: "Uploaded",
    parsed: "Parsed",
    chunked: "Chunked",
    contextualized: "Contextualized",
    embedded: "Embedded",
    indexed: "Indexed",
    done: "Done",
    running: "In progress",
    pending: "Waiting",
    failed: "Failed",
    live: "Live updates on",
    polling: "Checking for updates…",
    progress: "{{percent}}% complete",
  },
  versions: {
    title: "Version history",
    current: "Current version",
    live: "Live",
    chunks_one: "{{count}} chunk",
    chunks_other: "{{count}} chunks",
    restore: "Restore",
    restoreLabel: "Restore version {{version}}",
    restoring: "Restoring v{{version}} — re-indexing; the live version stays until it finishes",
    empty: "No earlier versions",
  },
  tabs: {
    label: "Source content",
    chunks: "Chunks",
    preview: "Preview",
    questions: "Questions it answers",
    usage: "Usage",
  },
  chunks: {
    search: "Search inside chunks…",
    view: "Chunk view",
    all: "All",
    excludedView: "Excluded",
    page: "p. {{page}}",
    pages: "pp. {{from}}–{{to}}",
    tokens: "{{count}} tok",
    excludedBadge: "Excluded",
    edited: "Edited",
    contextLabel: "Context added for retrieval",
    edit: "Edit",
    editLabel: "Edit chunk #{{n}}",
    exclude: "Exclude",
    excludeLabel: "Exclude chunk #{{n}}",
    include: "Include",
    includeLabel: "Include chunk #{{n}}",
    excludedToast: "Chunk #{{n}} excluded — it will no longer be retrieved",
    includedToast: "Chunk #{{n}} included again",
    test: "Test in Playground",
    showing: "Showing chunks 1–{{shown}} of {{total}}",
    loadMore: "Load more",
    emptyTitle: "No chunks yet",
    emptyBody: "Chunks appear here once processing finishes.",
    noMatchTitle: "No chunks match",
    noMatchBody: "Try another word, or clear the search.",
    noExcludedTitle: "No excluded chunks",
    noExcludedBody: "Exclude a chunk to stop it from being retrieved. Excluded chunks show here.",
    clearSearch: "Clear search",
  },
  editChunk: {
    title: "Edit chunk #{{n}}",
    subtitle: "{{path}} · page {{page}}",
    context: "Context added for retrieval",
    contextHint: "Generated for retrieval and prepended before embedding. Not shown to users.",
    text: "Chunk text",
    textRequired: "The chunk can't be empty",
    textTooLong: "Keep the chunk under {{max}} characters",
    warning: "Manual edits are kept when you re-index, but replaced if you upload a new version of the file.",
    save: "Save & re-embed",
    saved: "Chunk saved — re-embedded and live in answers",
  },
  preview: {
    pageOf: "Page {{page}} of {{total}}",
    highlighted: "Chunks on this page: {{chunks}}",
    noChunksOnPage: "No loaded chunks start on this page",
    previous: "Previous page",
    next: "Next page",
    frameTitle: "Page {{page}} of {{name}}",
    imageAlt: "Page {{page}} of {{name}}",
    unavailableTitle: "No preview for this page",
    unavailableBody: "Previews are available for PDF pages and scanned images.",
    noPagesTitle: "No page preview",
    noPagesBody: "This source has no pages to preview. Its chunks are listed under Chunks.",
  },
  questions: {
    emptyTitle: "Real questions will appear here",
    emptyBody: "Questions people asked that were answered with this source, with how helpful the answers were. {{count}} answers have cited it so far.",
    improve: "Open Knowledge Gaps",
  },
  usage: {
    citations: "Citations",
    agents: "Agents using it",
    chunks: "Active chunks",
    tokens: "Tokens indexed",
    storage: "Storage",
    generatedQuestions: "Generated questions",
    byAgent: "Agents",
    noAgents: "Link this source to an agent to start answering with it.",
    trendNote: "Citations per day, top chunks and channel split appear once usage history is available.",
  },
  replace: {
    title: "Replace file",
    body: "Uploading a new file creates v{{next}}. v{{current}} stays live until v{{next}} finishes processing, and you can restore it anytime.",
    queued: "Uploaded — processing v{{version}}",
    unchanged: "This file is identical to the live version — nothing to re-process",
    infected: "This file was blocked by the virus scan",
    failed: "The upload didn't finish. Try again.",
    dropTitle: "Drop the new file here, or click to browse",
    dropHint: "PDF, DOCX, XLSX, PPTX, CSV, TXT, MD, JPG/PNG · max 50 MB",
    dragActive: "Release to upload",
    rejected: {
      type: "{{name}}: this file type isn't supported",
      size: "{{name}}: larger than 50 MB",
      empty: "{{name}}: the file is empty",
      count: "{{name}}: upload one file at a time",
    },
    close: "Close",
  },
  delete: {
    title: "Delete {{name}}?",
    body_one: "All {{chunks}} chunks and {{versions}} versions are removed from {{count}} agent. It has been cited in {{citations}} answers — those questions may become knowledge gaps.",
    body_other: "All {{chunks}} chunks and {{versions}} versions are removed from {{count}} agents. It has been cited in {{citations}} answers — those questions may become knowledge gaps.",
    confirm: "Delete document",
    deleted: "{{name}} deleted",
  },
} as const;
